/**
 * Support - what the site costs, and how to help with it
 * ------------------------------------------------------
 * Two jobs:
 *   1. Somewhere a poster can donate whenever they like, not only when the
 *      prompt appears after finishing a chore.
 *   2. Where Stripe sends people back to afterwards, with ?session_id=... on
 *      a successful payment or ?cancelled=1 if they changed their mind.
 *
 * The page leads with the real numbers rather than a vague appeal, because
 * people give to something specific and because it is true. Posters only -
 * the route is wrapped in <ProtectedRoute role="user">, and the API refuses
 * any other role regardless.
 *
 * "ANOTHER AMOUNT" - added 16 September 2026, when donations went live. The
 * plan always had it (DONATIONS_PLAN.md section 3: three suggested amounts
 * "plus free entry with a €3 minimum") and the page only ever had the three
 * buttons. It lives here and not in the pop-up after a chore, which stays
 * short on purpose - the pop-up links here instead.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Heart, CheckCircle2 } from 'lucide-react';

import { donationApi } from '../services/api';
import Loader from '../components/Loader';
import { useI18n } from '../hooks/useI18n';

const euros = (cents) => `€${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

/**
 * Reads what somebody typed as euros, and gives back cents - or null if it is
 * not a sensible number.
 *
 * Accepts a comma as well as a point, because in Italy "7,50" IS seven euros
 * fifty, and reading it as seven hundred and fifty is the kind of mistake that
 * reaches a card. Stripe would be asked for exactly what this returns, so it
 * is strict: digits, one separator, at most two decimals.
 */
function parseEuros(text) {
  const clean = String(text || '').trim().replace(',', '.');
  if (!/^\d{1,4}(\.\d{1,2})?$/.test(clean)) return null;
  return Math.round(Number(clean) * 100);
}

export default function Support() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get('session_id');
  const cancelled = params.get('cancelled');

  const [config, setConfig] = useState(null);
  const [thanks, setThanks] = useState(null); // { amountCents }
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [other, setOther] = useState('');

  useEffect(() => {
    donationApi
      .config()
      .then(setConfig)
      .catch((err) => setError(err.message));
  }, []);

  // Coming back from Stripe. The webhook is the real record; this just means
  // the thank-you appears straight away rather than whenever Stripe calls.
  useEffect(() => {
    if (!sessionId) return;
    donationApi
      .verify(sessionId)
      .then((res) => {
        if (res.paid) setThanks({ amountCents: res.amountCents });
        // Cards settle at once, and cards are all that is offered - so this
        // is rare. But "thank you" for money that has not arrived would be a
        // small lie, and saying nothing would look like the page is broken.
        else setUnconfirmed(true);
        // Drop the id from the address bar so a refresh does not re-check it.
        setParams({}, { replace: true });
      })
      .catch((err) => setError(err.message));
  }, [sessionId, setParams]);

  const give = async (amountCents) => {
    setBusy(true);
    setError('');
    try {
      const { url } = await donationApi.checkout(amountCents);
      window.location.href = url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const minCents = config?.minCents ?? 300;
  const maxCents = config?.maxCents ?? 20000;
  const limits = { min: euros(minCents), max: euros(maxCents) };
  const otherCents = parseEuros(other);
  const otherValid = otherCents !== null && otherCents >= minCents && otherCents <= maxCents;
  // Only complain once something has been typed - an empty box is not wrong.
  const otherWrong = other.trim() !== '' && !otherValid;

  const giveOther = (event) => {
    event.preventDefault();
    if (otherValid) give(otherCents);
  };

  if (!config && !error) return <Loader label={t('common.loading')} />;

  return (
    <div className="container section">
      <div className="page-header">
        <h1>{t('support.title')}</h1>
        <p>{t('support.subtitle')}</p>
      </div>

      {thanks && (
        <div className="alert alert-success" style={{ marginBottom: 'var(--space-md)' }}>
          <CheckCircle2 size={20} aria-hidden="true" />
          <div>
            <strong>{t('support.thanks')}</strong>{' '}
            {t('support.thanksBody', { amount: euros(thanks.amountCents) })}
          </div>
        </div>
      )}

      {unconfirmed && !thanks && (
        <div className="alert" style={{ marginBottom: 'var(--space-md)' }}>
          {t('support.notConfirmedYet')}
        </div>
      )}

      {cancelled && !thanks && (
        <div className="alert" style={{ marginBottom: 'var(--space-md)' }}>
          {t('support.noPaymentTaken')}
        </div>
      )}

      {error && <div className="alert alert-error" style={{ marginBottom: 'var(--space-md)' }}>{error}</div>}

      <div className="card" style={{ marginBottom: 'var(--space-lg)' }}>
        <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('support.whereMoneyGoes')}</h3>
        <p>
          {t('support.notACompany')}
        </p>
        {/* The running cost used to be spelled out here as a figure. Taken out
            on request - the site does not put a number on what it costs to
            keep online anywhere. The point of this page is that donations go
            on running it and nowhere else, which stands without one. */}
        <p style={{ marginTop: 'var(--space-sm)' }}>
          {t('support.whatItCosts')}
        </p>
      </div>

      {config?.enabled ? (
        <div className="card">
          <h3 style={{ marginBottom: 'var(--space-sm)' }}>{t('support.chipIn')}</h3>

          {config.paymentsReady === false && (
            <div className="alert alert-warning" style={{ marginBottom: 'var(--space-sm)' }}>
              <div>{t('support.devNoStripe')}</div>
            </div>
          )}

          <div className="row" style={{ gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
            {(config.suggestedCents || []).map((cents) => (
              <button
                key={cents}
                type="button"
                className="btn btn-primary btn-inline"
                disabled={busy}
                onClick={() => give(cents)}
              >
                <Heart size={16} aria-hidden="true" /> {euros(cents)}
              </button>
            ))}
          </div>

          {/* A form, so Enter on a phone keyboard gives, the same as the
              button. The box takes the room and the button stays its own
              size - on a narrow screen they would otherwise swap roles. */}
          <form className="field donate-other" onSubmit={giveOther}>
            <label htmlFor="donate-other" className="label">{t('support.otherAmount')}</label>
            <div className="donate-other-row">
              <input
                id="donate-other"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={other}
                onChange={(event) => setOther(event.target.value)}
                placeholder="15"
                aria-invalid={otherWrong}
                aria-describedby="donate-other-hint"
              />
              <button type="submit" className="btn btn-outline btn-inline" disabled={busy || !otherValid}>
                <Heart size={16} aria-hidden="true" /> {t('support.give')}
              </button>
            </div>
            <span id="donate-other-hint" className={otherWrong ? 'field-error' : 'hint'}>
              {otherWrong ? t('support.otherAmountInvalid', limits) : t('support.otherAmountHint', limits)}
            </span>
          </form>

          <p className="text-sm text-muted" style={{ marginTop: 'var(--space-sm)' }}>
            {t('support.oneOffNoBadge')}
          </p>
        </div>
      ) : (
        <div className="card">
          <p className="text-muted">
            {t('support.notSwitchedOn')}
          </p>
        </div>
      )}
    </div>
  );
}
